<template>
  <div class="viq-expansion">
    <div class="viq-expansion__header">
      <h3 class="viq-expansion__title">Expansion Opportunity</h3>
      <el-button v-if="step === 'form'" size="small" @click="goBack">Back</el-button>
    </div>

    <!-- Form -->
    <div v-if="step === 'form'" class="viq-expansion__form">
      <p class="viq-expansion__desc">
        Evaluate expansion opportunities with market readiness heatmaps, regulatory paths, and risk rankings.
      </p>

      <div class="viq-expansion__field">
        <label>Current Operating Jurisdictions</label>
        <el-input v-model="currentMarkets" placeholder="e.g., United Kingdom, Germany" />
      </div>

      <div class="viq-expansion__field">
        <label>Target Markets (optional)</label>
        <el-input v-model="targetMarkets" placeholder="e.g., France, Netherlands, Singapore" />
      </div>

      <div class="viq-expansion__field">
        <label>Business Type / Product</label>
        <el-input v-model="businessType" placeholder="e.g., Cross-border payment services" />
      </div>

      <div class="viq-expansion__form-actions">
        <el-button
          type="primary"
          :disabled="!currentMarkets.trim()"
          :loading="loadingBaseline"
          @click="fetchBaseline"
        >
          Analyse Opportunities
        </el-button>
      </div>
    </div>

    <!-- Baseline + stream results -->
    <div v-else class="viq-expansion__results">
      <!-- Baseline data -->
      <div v-if="expansionBaseline" class="viq-expansion__baseline">
        <h4>Baseline Assessment</h4>
        <div v-if="expansionBaseline.markets" class="viq-expansion__heatmap">
          <div
            v-for="(market, i) in expansionBaseline.markets"
            :key="i"
            class="viq-expansion__market"
          >
            <span class="viq-expansion__market-name">{{ market.name }}</span>
            <span
              class="viq-expansion__market-score"
              :class="scoreClass(market.readiness_score)"
            >
              {{ market.readiness_score || 'N/A' }}
            </span>
            <span class="viq-expansion__market-label">{{ market.readiness_label || '' }}</span>
          </div>
        </div>
      </div>

      <!-- Streaming detailed results -->
      <div v-if="isStreaming || streamingText" class="viq-expansion__stream">
        <div v-if="isStreaming" class="viq-expansion__streaming">
          <span class="viq-expansion__spinner" />
          Generating detailed analysis...
        </div>
        <div
          v-if="streamingText || reportContent"
          class="viq-expansion__content"
          v-html="renderedContent"
        />
      </div>

      <div v-if="!isStreaming && (expansionBaseline || reportContent)" class="viq-expansion__actions">
        <el-button type="primary" @click="$emit('export-pdf')">Export as PDF</el-button>
        <el-button v-if="$config.features && $config.features.viqShare" @click="share">Save &amp; Share</el-button>
        <el-button @click="reset">New Analysis</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ViqExpansionOpportunity",
  data() {
    return {
      step: "form",
      currentMarkets: "",
      targetMarkets: "",
      businessType: "",
      loadingBaseline: false,
      reportContent: "",
    };
  },
  computed: {
    ...mapState("Viq", ["isStreaming", "streamingText", "expansionBaseline"]),
    renderedContent() {
      const text = this.reportContent || this.streamingText || "";
      if (!text) return "";
      let html = text;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
  watch: {
    isStreaming(val, old) {
      if (old && !val && this.step === "results") {
        this.reportContent = this.streamingText;
      }
    },
  },
  methods: {
    goBack() {
      this.$store.commit("Viq/SET_REPORT_STEP", 0);
      this.$store.commit("Viq/SET_REPORT_TYPE", null);
    },
    async fetchBaseline() {
      this.loadingBaseline = true;
      this.step = "results";

      const payload = {
        current_markets: this.currentMarkets.trim(),
        target_markets: this.targetMarkets.trim() || undefined,
        business_type: this.businessType.trim() || undefined,
      };

      await this.$store.dispatch("Viq/fetchExpansionBaseline", payload);
      this.loadingBaseline = false;

      // Then stream detailed results
      this.$store.dispatch("Viq/streamExpansion", payload);
    },
    scoreClass(score) {
      if (!score) return "";
      if (score >= 80) return "viq-expansion__market-score--high";
      if (score >= 50) return "viq-expansion__market-score--medium";
      return "viq-expansion__market-score--low";
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "expansion",
        baseline: this.expansionBaseline,
        content: this.reportContent,
      });
    },
    reset() {
      this.step = "form";
      this.currentMarkets = "";
      this.targetMarkets = "";
      this.businessType = "";
      this.reportContent = "";
      this.$store.commit("Viq/SET_EXPANSION_BASELINE", null);
      this.$store.commit("Viq/SET_EXPANSION_RESULTS", null);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-expansion {
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
  padding: $viq-space-lg 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $viq-space-lg;
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__desc {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
    line-height: 1.5;
  }

  &__form {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
  }

  &__field {
    margin-bottom: $viq-space-md;

    label {
      display: block;
      font-size: $viq-font-sm;
      font-weight: 600;
      color: $viq-text-primary;
      margin-bottom: $viq-space-sm;
    }
  }

  &__form-actions {
    margin-top: $viq-space-lg;
    display: flex;
    justify-content: flex-end;
  }

  &__baseline {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    margin-bottom: $viq-space-lg;

    h4 {
      font-size: $viq-font-md;
      font-weight: 700;
      color: $viq-text-primary;
      margin: 0 0 $viq-space-md;
    }
  }

  &__heatmap {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
  }

  &__market {
    display: flex;
    align-items: center;
    gap: $viq-space-md;
    padding: $viq-space-sm $viq-space-md;
    background: $viq-bg-secondary;
    border-radius: $viq-radius-sm;

    &-name {
      flex: 1;
      font-size: $viq-font-base;
      font-weight: 600;
      color: $viq-text-primary;
    }

    &-score {
      font-size: $viq-font-sm;
      font-weight: 700;
      padding: 2px 10px;
      border-radius: $viq-radius-full;

      &--high {
        background: rgba(16, 185, 129, 0.1);
        color: $viq-success;
      }
      &--medium {
        background: rgba(245, 158, 11, 0.1);
        color: $viq-warning;
      }
      &--low {
        background: rgba(239, 68, 68, 0.1);
        color: $viq-error;
      }
    }

    &-label {
      font-size: $viq-font-xs;
      color: $viq-text-secondary;
      min-width: 80px;
    }
  }

  &__streaming {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-md;
    color: $viq-text-secondary;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    ::v-deep {
      h2, h3 { margin: $viq-space-lg 0 $viq-space-sm; font-weight: 700; }
      p { margin: 0 0 $viq-space-sm; }
      ul { padding-left: $viq-space-lg; margin: $viq-space-sm 0; }
      li { margin-bottom: $viq-space-xs; }
      strong { font-weight: 700; }
    }
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
    margin-top: $viq-space-lg;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
